import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import axios from 'axios';
import { deduplicateRequest } from '../utils/requestCache';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

interface CustomerAccount {
  customerAccountId: number;
  customerId: number;
  phone: string;
  email?: string;
}

interface Customer {
  id: number;
  first_name: string;
  last_name: string;
  company_name?: string;
  email: string;
  phone: string;
  created_at: string;
}

interface CustomerData {
  customerAccount: CustomerAccount;
  customer: Customer;
  stats: {
    vehicles: number;
    estimates: number;
  };
}

interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

interface CustomerAuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  customerData: CustomerData | null;
  accessToken: string | null;
  login: (identifier: string, password: string) => Promise<void>;
  register: (options: { phone?: string; email?: string; password: string; customerId: number; referralCode?: string }) => Promise<void>;
  logout: () => Promise<void>;
  refreshAuth: () => Promise<void>;
}

const CustomerAuthContext = createContext<CustomerAuthContextType | undefined>(undefined);

const TOKEN_KEY = 'customer_access_token';
const REFRESH_TOKEN_KEY = 'customer_refresh_token';

export const CustomerAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [customerData, setCustomerData] = useState<CustomerData | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const loadingRef = useRef(false); // Prevent concurrent initAuth calls

  // Initialize auth state on mount with deduplication and abort controller
  const initAuth = useCallback(async () => {
    // Prevent concurrent calls (important for React StrictMode)
    if (loadingRef.current) {
      console.log('[CustomerAuthContext] initAuth already in progress, skipping...');
      return;
    }

    loadingRef.current = true;

    const token = localStorage.getItem(TOKEN_KEY);
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

    if (token && refreshToken) {
      setAccessToken(token);
      try {
        console.log('[CustomerAuthContext] Loading customer data...');

        // Deduplicate the request
        const data = await deduplicateRequest('customer-auth:me', async () => {
          const response = await axios.get(`${API_URL}/customer-auth/me`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          return response.data;
        });

        setCustomerData(data);
        setIsAuthenticated(true);
      } catch (error: any) {
        console.error('[CustomerAuthContext] Failed to load customer auth:', error);

        // Don't clear tokens on rate limit errors
        if (!error.isRateLimit && error.response?.status !== 429) {
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(REFRESH_TOKEN_KEY);
        }
      }
    } else {
      console.log('[CustomerAuthContext] No customer tokens found');
    }

    setIsLoading(false);
    loadingRef.current = false;
  }, []);

  // Load auth state on mount with cleanup
  useEffect(() => {
    const abortController = new AbortController();

    initAuth();

    return () => {
      // Cleanup: abort any in-flight requests
      abortController.abort();
    };
  }, [initAuth]);

  const login = async (identifier: string, password: string) => {
    try {
      const response = await axios.post(`${API_URL}/customer-auth/login`, {
        identifier,
        password,
      });

      const { accessToken, refreshToken, customerAccount, customer } = response.data;

      // Store tokens
      localStorage.setItem(TOKEN_KEY, accessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
      setAccessToken(accessToken);

      // Set auth state
      setIsAuthenticated(true);
      setCustomerData(response.data);
    } catch (error: any) {
      console.error('Customer login error:', error);
      throw new Error(error.response?.data?.error || 'Login failed');
    }
  };

  const register = async (options: { phone?: string; email?: string; password: string; customerId: number; referralCode?: string }) => {
    try {
      const response = await axios.post(`${API_URL}/customer-auth/register`, options);

      const { accessToken, refreshToken } = response.data;

      // Store tokens
      localStorage.setItem(TOKEN_KEY, accessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
      setAccessToken(accessToken);

      // Set auth state
      setIsAuthenticated(true);
      setCustomerData(response.data);
    } catch (error: any) {
      console.error('Customer registration error:', error);
      throw new Error(error.response?.data?.error || 'Registration failed');
    }
  };

  const logout = async () => {
    try {
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (refreshToken) {
        await axios.post(`${API_URL}/customer-auth/logout`, {
          refreshToken,
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear tokens and state regardless of API call success
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      setAccessToken(null);
      setIsAuthenticated(false);
      setCustomerData(null);
    }
  };

  const refreshAuth = async () => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      if (token) {
        const response = await axios.get(`${API_URL}/customer-auth/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setCustomerData(response.data);
      }
    } catch (error) {
      console.error('Failed to refresh customer data:', error);
    }
  };

  return (
    <CustomerAuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        customerData,
        accessToken,
        login,
        register,
        logout,
        refreshAuth,
      }}
    >
      {children}
    </CustomerAuthContext.Provider>
  );
};

export const useCustomerAuth = () => {
  const context = useContext(CustomerAuthContext);
  if (context === undefined) {
    throw new Error('useCustomerAuth must be used within a CustomerAuthProvider');
  }
  return context;
};
